import React from 'react';
import styled from 'styles/styled';

export interface FlowTagProps {
  /** 'from' = sender (your wallet), 'to' = receiver (Trading Battles) */
  kind: 'from' | 'to';
}

/**
 * Context pill aligned with
 * trading-battles-design-system 3/project/preview/components-inputs-directional.html
 * .ctx-pill spec — 12px / 500 weight / 0.04em letter-spacing,
 * border opacity 0.40 on both variants. Recipient (`to`) bg drops to
 * --tb-bull-soft-bg 0.06 (matches the soft-tinted surface used by
 * status-pill); sender (`from`) keeps the legacy 0.10 purple bg.
 */
const Tag = styled.span<{ kind: 'from' | 'to' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  ${({ kind }) =>
    kind === 'from'
      ? `
        background: rgba(70, 66, 255, 0.10);
        border: 1px solid rgba(70, 66, 255, 0.40);
        color: #C7C5FF;
      `
      : `
        background: rgba(82, 211, 129, 0.06);
        border: 1px solid rgba(82, 211, 129, 0.40);
        color: #52D381;
      `}
`;

const Dot = styled.span<{ kind: 'from' | 'to' }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ kind }) =>
    kind === 'from'
      ? `
        background: #4642FF;
        color: #fff;
      `
      : `
        background: #52D381;
        color: #0A0A0A;
      `}
  svg {
    width: 11px;
    height: 11px;
  }
`;

const ArrowUpRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

const ArrowDownLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
    <line x1="17" y1="7" x2="7" y2="17" />
    <polyline points="17 17 7 17 7 7" />
  </svg>
);

const Wrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
`;

const FlowTag: React.FC<FlowTagProps> = ({ kind }) => (
  <Wrap>
    <Tag kind={kind}>
      <Dot kind={kind} aria-hidden="true">
        {kind === 'from' ? <ArrowUpRight /> : <ArrowDownLeft />}
      </Dot>
      {kind === 'from' ? 'FROM · YOUR WALLET' : 'TO · TRADING BATTLES'}
    </Tag>
  </Wrap>
);

export default FlowTag;
